import React, { useState, useEffect, useRef } from 'react';
import { BsPencilSquare, BsPencil, BsChatDots, BsListTask, BsGear, BsGrid3X3Gap } from "react-icons/bs";
import { User } from 'firebase/auth';
import NotesTab from './tabs/NotesTab';
import ChatTab from './tabs/ChatTab';
import './ToolsPanel.css';

// Define available tool types
export type ToolType = 'notes' | 'chat' | 'tasks' | 'settings';

// Tool configuration interface
interface ToolConfig {
  id: ToolType;
  name: string;
  icon: React.ReactNode;
  component: React.ComponentType<any>;
  requiresSubscription?: boolean;
}

interface ToolsPanelProps {
  user: User | null;
  hasPaidSubscription: boolean;
  onShowPaymentPopup: () => void;
  onShowLoginInfo: () => void;
  isWalkthroughMode?: boolean;
  // Required props for external control
  isOpen: boolean;
  onToggle: React.Dispatch<React.SetStateAction<boolean>>;
  activeTool: ToolType;
  onShowToolPicker: () => void;
  onToolSelect: (toolId: ToolType) => void;
  enabledTools: { [key: string]: boolean };
}

const ToolsPanel: React.FC<ToolsPanelProps> = ({
  user,
  hasPaidSubscription,
  onShowPaymentPopup,
  onShowLoginInfo,
  isWalkthroughMode = false,
  isOpen,
  onToggle,
  activeTool,
  onShowToolPicker,
  onToolSelect,
  enabledTools
}) => {
  // Panel position and dragging
  const [panelPosition, setPanelPosition] = useState({ edge: 'right', position: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Refs
  const panelRef = useRef<HTMLDivElement>(null);
  const tabButtonRef = useRef<HTMLDivElement>(null);

  // Tool configurations
  const toolConfigs: ToolConfig[] = [
    {
      id: 'notes',
      name: 'Notes',
      icon: <BsPencil />,
      component: NotesTab,
      requiresSubscription: true
    },
    {
      id: 'chat',
      name: 'Community Chat',
      icon: <BsChatDots />,
      component: ChatTab,
      requiresSubscription: true
    },
    // Future tools can be added here:
    // {
    //   id: 'tasks',
    //   name: 'Tasks',
    //   icon: <BsListTask />,
    //   component: TasksTab,
    //   requiresSubscription: true
    // }
  ];

  // Filter tools based on enabled settings
  const availableTools = toolConfigs.filter(tool => enabledTools[tool.id] !== false);
  
  // Don't show any fallback - if no tools are enabled, don't show the tab container
  const finalToolConfigs = availableTools;

  // Get the current active tool config - only if there are available tools
  const activeToolConfig = finalToolConfigs.length > 0 
    ? (finalToolConfigs.find(tool => tool.id === activeTool) || finalToolConfigs[0])
    : null;

  // Load saved state on mount
  useEffect(() => {
    // Load panel position
    const savedPosition = localStorage.getItem('vibeToolsPanelPosition');
    if (savedPosition) {
      try {
        setPanelPosition(JSON.parse(savedPosition));
      } catch (e) {
        console.error('Failed to parse saved panel position:', e);
      }
    }

    // Load pinned state
    const savedPinned = localStorage.getItem('vibeToolsPanelPinned');
    if (savedPinned === 'true') {
      setIsPinned(true);
    }
  }, []);

  // Save state changes
  useEffect(() => {
    localStorage.setItem('vibeToolsPanelPosition', JSON.stringify(panelPosition));
  }, [panelPosition]);

  useEffect(() => {
    localStorage.setItem('vibeToolsPanelPinned', String(isPinned));
  }, [isPinned]);

  // Handle clicks outside panel to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isOpen && 
          panelRef.current && 
          !panelRef.current.contains(event.target as Node) &&
          !isPinned) {
        // Close the panel
        onToggle(false);
        // Also exit fullscreen mode if it was active
        if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isPinned, isFullscreen, onToggle]);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Calculate distance to each edge
      const distToLeft = e.clientX;
      const distToRight = windowWidth - e.clientX;
      const distToTop = e.clientY;
      const distToBottom = windowHeight - e.clientY;
      
      // Find the closest edge
      const minDistance = Math.min(distToLeft, distToRight, distToTop, distToBottom);
      
      let newEdge: 'left' | 'right' | 'top' | 'bottom';
      let newPosition: number;
      
      if (minDistance === distToLeft) {
        newEdge = 'left';
        newPosition = (e.clientY / windowHeight) * 100;
      } else if (minDistance === distToRight) {
        newEdge = 'right';
        newPosition = (e.clientY / windowHeight) * 100;
      } else if (minDistance === distToTop) {
        newEdge = 'top';
        newPosition = (e.clientX / windowWidth) * 100;
      } else {
        newEdge = 'bottom';
        newPosition = (e.clientX / windowWidth) * 100;
      }
      
      // Clamp position
      newPosition = Math.min(Math.max(newPosition, 10), 90);
      
      setPanelPosition({ edge: newEdge, position: newPosition });
    };
    
    const handleMouseUp = () => {
      setPanelPosition(prev => ({ edge: prev.edge, position: 50 }));
      setIsDragging(false);
    };
    
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Toggle panel with subscription check
  const togglePanel = () => {
    // Instead of opening the panel, open the tool picker
    onShowToolPicker();
  };

  // Handle tool selection directly
  const handleToolSelect = (toolId: ToolType) => {
    const toolConfig = toolConfigs.find(tool => tool.id === toolId);
    if (!toolConfig) return;

    // Check subscription requirements
    if (!isWalkthroughMode && toolConfig.requiresSubscription) {
      if (!user) {
        onShowLoginInfo();
        return;
      }
      if (!hasPaidSubscription) {
        onShowPaymentPopup();
        return;
      }
    }

    // If this tool is already active and panel is open, close it
    if (activeTool === toolId && isOpen) {
      onToggle(false);
    } else {
      // Switch to this tool and open the panel
      onToolSelect(toolId);
    }
  };

  // Get panel position styles
  const getPanelPositionStyle = () => {
    // In fullscreen mode, don't apply position styles - let CSS handle it
    if (isFullscreen) {
      return {};
    }
    
    const safetyMargin = 5;
    
    if (panelPosition.edge === 'left' || panelPosition.edge === 'right') {
      let topPos = panelPosition.position - 30;
      topPos = Math.max(topPos, safetyMargin);
      topPos = Math.min(topPos, 100 - 60 - safetyMargin);
      return { top: `${topPos}%` };
    } else {
      let leftPos = panelPosition.position - 30;
      leftPos = Math.max(leftPos, safetyMargin);
      leftPos = Math.min(leftPos, 100 - 60 - safetyMargin);
      return { left: `${leftPos}%` };
    }
  };

  // Get tab container position styles
  const getTabContainerProps = () => {
    const baseClass = `tools-tab-container edge-${panelPosition.edge}`;
    const dragClass = isDragging ? 'dragging' : '';
    
    let style: React.CSSProperties = {};
    
    switch (panelPosition.edge) {
      case 'left':
        style = { 
          left: 0,
          top: `${panelPosition.position}%`,
          transform: 'translateY(-50%)',
          flexDirection: 'column'
        };
        break;
      case 'right':
        style = { 
          right: 0,
          top: `${panelPosition.position}%`,
          transform: 'translateY(-50%)',
          flexDirection: 'column'
        };
        break;
      case 'top':
        style = { 
          top: 0,
          left: `${panelPosition.position}%`,
          transform: 'translateX(-50%)',
          flexDirection: 'row'
        };
        break;
      case 'bottom':
        style = { 
          bottom: 0,
          left: `${panelPosition.position}%`,
          transform: 'translateX(-50%)',
          flexDirection: 'row'
        };
        break;
    }
    
    return {
      className: `${baseClass} ${dragClass}`,
      style
    };
  };

  // Render the active tool component
  const renderActiveTool = () => {
    const ActiveComponent = activeToolConfig?.component;
    
    // If no active tool config, don't render anything
    if (!ActiveComponent) {
      return null;
    }
    
    // Pass common props to all tool components
    const commonProps = {
      user,
      hasPaidSubscription,
      onShowPaymentPopup,
      onShowLoginInfo,
      isWalkthroughMode,
      isPinned,
      onTogglePin: setIsPinned,
      isFullscreen,
      onToggleFullscreen: setIsFullscreen
    };

    return <ActiveComponent {...commonProps} />;
  };

  return (
    <>
      {/* Tools Panel */}
      <div 
        ref={panelRef}
        className={`tools-panel ${isOpen ? 'open' : ''} panel-edge-${panelPosition.edge} ${isPinned ? 'pinned' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
        style={getPanelPositionStyle()}
      >
        {/* Tool Content */}
        <div className="tools-content">
          {renderActiveTool()}
        </div>
      </div>

      {/* Minimized Tab Button - only show when panel is closed and tools are available */}
      {!isOpen && finalToolConfigs.length > 0 && (
        <div 
          ref={tabButtonRef}
          {...getTabContainerProps()}
        >
          {finalToolConfigs.map((tool) => (
            <div
              key={tool.id}
              className={`tools-tab-item ${activeTool === tool.id ? 'active' : ''}`}
              onClick={() => handleToolSelect(tool.id)}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              title={tool.name}
            >
              {tool.icon}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ToolsPanel; 